import React, { useState, useEffect } from 'react';
import LoadingBar from 'react-top-loading-bar';
import LoadingGif from '../../assets/loading.gif';

interface LoadingProps {
  height?: string;
  type?: 'table' | 'cards' | 'auto';
}

const Loading: React.FC<LoadingProps> = ({ height = '80vh', type = 'auto' }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Simulate progress
    const timer = setInterval(() => {
      setProgress(oldProgress => {
        if (oldProgress >= 99) {
          clearInterval(timer);
          return 99;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 99);
      });
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <div
      className="relative w-full flex flex-col items-center justify-center overflow-hidden"
      style={{ height }}
    >
      {/* Top Loading Bar */}
      <LoadingBar
        color="#2563eb" // blue-600
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
        height={3}
        shadow={true}
      />

      {/* Skeleton Background - adapts based on type prop */}
      <div className="absolute inset-0 w-full h-full">
        {type === 'table' || type === 'auto' ? (
          // Table skeleton layout
          <div className="w-full mb-8 bg-gray-100/50 rounded-lg overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-5 gap-2 p-4 border-b border-gray-200/30">
              {Array(5)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={`th-${i}`}
                    className="h-8 bg-gray-200/50 rounded animate-pulse"
                    style={{ animationDelay: `${i * 50}ms` }}
                  ></div>
                ))}
            </div>

            {/* Table rows */}
            {Array(8)
              .fill(0)
              .map((_, i) => (
                <div
                  key={`tr-${i}`}
                  className="grid grid-cols-5 gap-2 p-4 border-b border-gray-200/30"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  {Array(5)
                    .fill(0)
                    .map((_, j) => (
                      <div
                        key={`td-${i}-${j}`}
                        className="h-6 bg-gray-200/50 rounded animate-pulse"
                        style={{ animationDelay: `${(i * 5 + j) * 30}ms` }}
                      ></div>
                    ))}
                </div>
              ))}
          </div>
        ) : null}

        {type === 'cards' || (type === 'auto' && !['table'].includes(type)) ? (
          // Cards grid skeleton
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 p-4">
            {Array(9)
              .fill(0)
              .map((_, i) => (
                <div
                  key={`card-${i}`}
                  className="bg-gray-100/50 rounded-lg p-4 h-40 flex flex-col justify-between animate-pulse"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="w-2/3 h-5 bg-gray-200/50 rounded mb-2"></div>
                  <div className="w-full h-20 bg-gray-200/50 rounded"></div>
                  <div className="flex justify-end">
                    <div className="w-20 h-6 bg-gray-200/50 rounded"></div>
                  </div>
                </div>
              ))}
          </div>
        ) : null}
      </div>

      {/* Subtle shimmer overlay effect */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer"
        style={{ backgroundSize: '200% 100%' }}
      ></div>

      {/* Center loading indicator with white background - minimalistic version */}
      <div className="relative z-10 flex flex-col items-center">
        <div className="bg-white p-5 rounded-lg shadow-md flex flex-col items-center">
          <img src={LoadingGif} alt="Loading..." className="w-12 h-12 mb-2" />
          <span className="text-gray-700 text-sm font-medium">
            Loading... {Math.round(progress)}%
          </span>

          {/* Progress bar */}
          <div className="w-32 h-1 bg-gray-100 rounded-full mt-2 overflow-hidden">
            <div
              className="h-full bg-blue-500/80 rounded-full"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Loading;
